/**
 * API client per la gestione dei veterinari.
 * 
 * Endpoints:
 * - GET /api/v1/vets/ - Lista veterinari (con filtro active opzionale)
 * - GET /api/v1/vets/{id} - Dettaglio veterinario
 * - POST /api/v1/vets/ - Crea veterinario
 * - PATCH /api/v1/vets/{id}/deactivate - Disattiva veterinario
 * - PATCH /api/v1/vets/{id}/activate - Attiva veterinario
 */

import api from './api';

export interface Vet {
  id: string;
  full_name: string;
  email: string;
  specialties: string[];
  gcal_calendar_id: string | null;
  is_active: boolean;
  open_cases_count?: number;
}

export interface VetCreate {
  full_name: string;
  email: string;
  specialties: string[];
}

export interface VetUpdate {
  full_name?: string;
  email?: string;
  specialties?: string[];
}

export const vetsApi = {
  /**
   * Lista tutti i veterinari attivi.
   */
  list: async (activeOnly: boolean = true): Promise<Vet[]> => {
    const params = { active_only: activeOnly ? 'true' : 'false' };
    const response = await api.get<Vet[]>('/vets/', { params });
    return response.data;
  },

  /**
   * Ottiene il dettaglio di un veterinario.
   */
  get: async (vetId: string): Promise<Vet> => {
    const response = await api.get<Vet>(`/vets/${vetId}`);
    return response.data;
  },

  /**
   * Crea un nuovo veterinario.
   */
  create: async (data: VetCreate): Promise<Vet> => {
    const response = await api.post<Vet>('/vets/', data);
    return response.data;
  },

  /**
   * Aggiorna un veterinario.
   */
  update: async (vetId: string, data: VetUpdate): Promise<Vet> => {
    const response = await api.patch<Vet>(`/vets/${vetId}`, data);
    return response.data;
  },

  /**
   * Disattiva un veterinario.
   */
  deactivate: async (vetId: string): Promise<void> => {
    await api.patch(`/vets/${vetId}/deactivate`);
  },

  /**
   * Attiva un veterinario.
   */
  activate: async (vetId: string): Promise<void> => {
    await api.patch(`/vets/${vetId}/activate`);
  },
};
